import { useEffect } from 'react'
import { toast } from 'sonner'
import { useEditSession } from '@/core/store/editSession'
import { selectCanUndo } from '@/core/store/selectors'

export const BACK_EXIT_WINDOW_MS = 2_000
export const HISTORY_STATE_KEY = 'insta-little-helper-edit-guard'

const NOTHING_TO_UNDO_MESSAGE = 'Nothing to undo — press back again to leave'
const NOTHING_TO_UNDO_TOAST_ID = 'back-button-nothing-to-undo'

function isGuardState(state: unknown): boolean {
  return typeof state === 'object'
    && state !== null
    && (state as Record<string, unknown>)[HISTORY_STATE_KEY] === true
}

function pushGuard(): void {
  window.history.pushState({ [HISTORY_STATE_KEY]: true }, '')
}

export function useBackButtonUndo(): void {
  const documentOpen = useEditSession((state) => state.document !== null)

  useEffect(() => {
    if (!documentOpen) return

    let exitWindowExpiresAt = 0
    let leaving = false
    if (!isGuardState(window.history.state)) pushGuard()

    const onPopState = () => {
      const state = useEditSession.getState()
      if (!state.document) return

      const canUndo = selectCanUndo(state)
      if (!canUndo && Date.now() < exitWindowExpiresAt) {
        leaving = true
        window.history.back()
        return
      }

      pushGuard()
      if (canUndo) {
        exitWindowExpiresAt = 0
        toast.dismiss(NOTHING_TO_UNDO_TOAST_ID)
        state.undo()
        return
      }

      exitWindowExpiresAt = Date.now() + BACK_EXIT_WINDOW_MS
      toast.info(NOTHING_TO_UNDO_MESSAGE, { id: NOTHING_TO_UNDO_TOAST_ID })
    }

    window.addEventListener('popstate', onPopState)
    return () => {
      window.removeEventListener('popstate', onPopState)
      if (!leaving && isGuardState(window.history.state)) window.history.back()
    }
  }, [documentOpen])
}
